import { Injectable, NgZone, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Centralized scroll service.
 *
 * All in-page scrolling MUST go through this service so there is
 * exactly one place that knows:
 *   1. Which DOM element is the scroll container (.main-container).
 *   2. How much offset to apply (scroll-margin-top or programmatic).
 *   3. How to wait for the DOM to settle before scrolling.
 *
 * The service also handles scroll-to-top on route changes, replacing
 * the three competing mechanisms that existed before.
 */
@Injectable({ providedIn: 'root' })
export class ScrollService {
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);

  /** Pixel offset to keep anchored content from sitting flush at the top. */
  private readonly SCROLL_BUFFER = 16;

  /**
   * Maximum number of animation-frame polls when waiting for an element
   * to appear in the DOM (≈ 3 seconds at 60 fps).
   */
  private readonly MAX_POLL_FRAMES = 180;

  private scrollContainer: HTMLElement | null = null;

  /**
   * Call once from the component that owns the scroll container
   * (AudienceManagerComponent) to register the element.
   */
  registerScrollContainer(el: HTMLElement): void {
    this.scrollContainer = el;
  }

  /**
   * Listen to NavigationEnd and reset scroll to top — but only when
   * the user navigates to a genuinely different page.
   *
   * Skips the reset when:
   *  - The URL has a fragment (#…) — fragment scroll is handled by components.
   *  - Only the last path segment changed (child/tab route switch within
   *    the same parent page, e.g. /data-reference/browse → /data-reference/recipes).
   */
  initRouteScrollReset(): void {
    let previousPath = '';

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(event => {
        const url = event.urlAfterRedirects;
        const hasFragment = url.includes('#');

        // Strip fragment and query string to get the bare path.
        const currentPath = url.split('#')[0].split('?')[0];

        // Parent = everything up to the last segment.
        const prevParent = parentPath(previousPath);
        const currParent = parentPath(currentPath);

        previousPath = currentPath;

        // Skip reset for fragment navigations.
        if (hasFragment) return;

        // Skip reset for sibling navigations (same parent, different leaf).
        // This keeps the viewport steady when the user switches tabs.
        if (prevParent && currParent && prevParent === currParent) return;

        this.scrollToTop('instant');
      });
  }

  // ────────────────────────────────────────────────────────────
  //  Public API
  // ────────────────────────────────────────────────────────────

  /**
   * Scroll the container to the very top.
   */
  scrollToTop(behavior: ScrollBehavior = 'instant'): void {
    this.scrollContainer?.scrollTo({ top: 0, left: 0, behavior });
  }

  /**
   * Scroll to a specific DOM element by its id.
   * The element must already be in the DOM.
   */
  scrollToElement(
    elementId: string,
    behavior: ScrollBehavior = 'smooth',
  ): void {
    const el = document.getElementById(elementId);
    if (!el || !this.scrollContainer) return;
    this.performScroll(el, behavior);
  }

  /**
   * Wait for the next Angular render cycle, then scroll to the element.
   * Replaces every `setTimeout(() => scrollIntoView(), 100)` pattern.
   *
   * Uses requestAnimationFrame polling so we are guaranteed the DOM
   * has been committed before we attempt the scroll.
   *
   * Returns a promise that resolves when the scroll fires (or rejects
   * if the element never appears within ~3 s).
   */
  scrollAfterRender(
    elementId: string,
    behavior: ScrollBehavior = 'smooth',
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let polls = 0;
      const poll = () => {
        const el = document.getElementById(elementId);
        if (el && this.scrollContainer) {
          this.performScroll(el, behavior);
          resolve();
          return;
        }
        polls++;
        if (polls >= this.MAX_POLL_FRAMES) {
          reject(
            new Error(
              `ScrollService: element #${elementId} not found after ${this.MAX_POLL_FRAMES} frames`,
            ),
          );
          return;
        }
        requestAnimationFrame(poll);
      };
      // Kick off on the next frame so Angular has a chance to commit.
      this.ngZone.runOutsideAngular(() => requestAnimationFrame(poll));
    });
  }

  // ────────────────────────────────────────────────────────────
  //  Internal
  // ────────────────────────────────────────────────────────────

  private performScroll(el: HTMLElement, behavior: ScrollBehavior): void {
    if (!this.scrollContainer) return;

    // Calculate position relative to the scroll container.
    const containerRect = this.scrollContainer.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const relativeTop =
      elRect.top - containerRect.top + this.scrollContainer.scrollTop;

    this.scrollContainer.scrollTo({
      top: Math.max(0, relativeTop - this.SCROLL_BUFFER),
      left: 0,
      behavior,
    });
  }
}

/** Return everything before the last `/` segment, e.g. `/a/b/c` → `/a/b`. */
function parentPath(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx > 0 ? path.substring(0, idx) : '';
}
